/**
 * Compact, colour-coded level pill used in profiles + recognition UI.
 *
 * Colours follow the level progression (newbie → legend) so the eye can
 * distinguish levels at a glance. Tooltip exposes the full progress range.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { LevelKey, PointsLevel } from "types/Points";

interface Props {
  level: PointsLevel;
  size?: "sm" | "md";
  showProgress?: boolean;
  /** Writes the karma range under the badge too: a touch screen has no tooltip to show it. */
  showThreshold?: boolean;
  className?: string;
}

const LEVEL_COLOURS: Record<LevelKey, { bg: string; text: string; ring: string; progress: string }> = {
  newbie:      { bg: "bg-gray-100",   text: "text-gray-700",   ring: "ring-gray-300",   progress: "bg-gray-700" },
  helper:      { bg: "bg-green-100",  text: "text-green-700",  ring: "ring-green-300",  progress: "bg-green-700" },
  contributor: { bg: "bg-blue-100",   text: "text-blue-700",   ring: "ring-blue-300",   progress: "bg-blue-700" },
  star:        { bg: "bg-purple-100", text: "text-purple-700", ring: "ring-purple-300", progress: "bg-purple-700" },
  champion:    { bg: "bg-amber-100",  text: "text-amber-700",  ring: "ring-amber-300",  progress: "bg-amber-700" },
  legend:      { bg: "bg-rose-100",   text: "text-rose-700",   ring: "ring-rose-300",   progress: "bg-rose-700" },
};

const LevelBadge: React.FC<Props> = ({ level, size = "md", showProgress, showThreshold, className = "" }) => {
  const { t } = useTranslation();
  const colour = LEVEL_COLOURS[level.key] || LEVEL_COLOURS.newbie;
  const padding = size === "sm" ? "px-1.5 py-0.5 text-[11px]" : "px-2 py-0.5 text-xs";

  const tooltip = level.thresholdNext
    ? `${level.thresholdMin}–${level.thresholdNext} ${t("karma_unit", { defaultValue: "karma" })}`
    : `${level.thresholdMin}+ ${t("karma_unit", { defaultValue: "karma" })}`;

  const badge = (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-semibold ring-1 ${colour.bg} ${colour.text} ${colour.ring} ${padding} ${className}`}
      title={tooltip}
    >
      <span>{t(`level_${level.key}`)}</span>
      <span className="opacity-70">L{level.level}</span>
      {showProgress && level.thresholdNext != null && (
        <span className="ml-1 inline-block h-1 w-10 bg-white/60 rounded-full overflow-hidden">
          <span
            className={`block h-full ${colour.progress}`}
            style={{ width: `${Math.round(level.progress * 100)}%` }}
          />
        </span>
      )}
    </span>
  );

  if (!showThreshold) return badge;
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      {badge}
      <span className="text-xs text-gray-500">{tooltip}</span>
    </span>
  );
};

export default LevelBadge;
